import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, Tag, ArrowRight, ShareNetwork, Copy } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import SEOHead from '../components/SEOHead';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import { usePublicT } from '../i18n/usePublicT';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function MarkdownRenderer({ content }) {
  if (!content) return null;

  const lines = content.split('\n');
  const elements = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.startsWith('### ')) {
      elements.push(<h3 key={i} className="text-lg font-bold text-white mt-8 mb-3">{line.slice(4)}</h3>);
    } else if (line.startsWith('## ')) {
      elements.push(<h2 key={i} className="text-xl font-bold text-white mt-10 mb-4">{line.slice(3)}</h2>);
    } else if (line.startsWith('# ')) {
      elements.push(<h1 key={i} className="text-2xl font-bold text-white mt-10 mb-4">{line.slice(2)}</h1>);
    } else if (line.startsWith('- ') || line.startsWith('* ')) {
      const items = [];
      while (i < lines.length && (lines[i].startsWith('- ') || lines[i].startsWith('* '))) {
        items.push(<li key={i} className="text-gray-300 leading-relaxed">{renderInline(lines[i].slice(2))}</li>);
        i++;
      }
      elements.push(<ul key={`ul-${i}`} className="list-disc list-inside space-y-1.5 my-4 ml-2">{items}</ul>);
      continue;
    } else if (/^\d+\.\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
        items.push(<li key={i} className="text-gray-300 leading-relaxed">{renderInline(lines[i].replace(/^\d+\.\s/, ''))}</li>);
        i++;
      }
      elements.push(<ol key={`ol-${i}`} className="list-decimal list-inside space-y-1.5 my-4 ml-2">{items}</ol>);
      continue;
    } else if (line.startsWith('> ')) {
      elements.push(
        <blockquote key={i} className="border-l-4 border-[#FF9900]/40 pl-4 my-6 italic text-gray-400">
          {renderInline(line.slice(2))}
        </blockquote>
      );
    } else if (line.startsWith('---') || line.startsWith('***')) {
      elements.push(<hr key={i} className="border-white/10 my-8" />);
    } else if (line.trim() === '') {
      elements.push(<div key={i} className="h-2" />);
    } else {
      elements.push(<p key={i} className="text-gray-300 leading-relaxed my-3">{renderInline(line)}</p>);
    }
    i++;
  }

  return <>{elements}</>;
}

function renderInline(text) {
  if (!text) return text;
  const parts = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
    const linkMatch = remaining.match(/\[(.+?)\]\((.+?)\)/);

    let nextMatch = null;
    let nextIndex = remaining.length;

    if (boldMatch && boldMatch.index < nextIndex) {
      nextMatch = { type: 'bold', match: boldMatch };
      nextIndex = boldMatch.index;
    }
    if (linkMatch && linkMatch.index < nextIndex) {
      nextMatch = { type: 'link', match: linkMatch };
      nextIndex = linkMatch.index;
    }

    if (!nextMatch) {
      parts.push(remaining);
      break;
    }

    if (nextIndex > 0) parts.push(remaining.slice(0, nextIndex));

    if (nextMatch.type === 'bold') {
      parts.push(<strong key={key++} className="font-semibold text-white">{nextMatch.match[1]}</strong>);
      remaining = remaining.slice(nextIndex + nextMatch.match[0].length);
    } else if (nextMatch.type === 'link') {
      parts.push(
        <a key={key++} href={nextMatch.match[2]} className="text-[#FF9900] hover:underline" target="_blank" rel="noopener noreferrer">
          {nextMatch.match[1]}
        </a>
      );
      remaining = remaining.slice(nextIndex + nextMatch.match[0].length);
    }
  }

  return parts;
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const pt = usePublicT();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const [postRes, relatedRes] = await Promise.all([
          axios.get(`${API_URL}/api/blog/posts/${slug}`),
          axios.get(`${API_URL}/api/blog/related/${slug}`).catch(() => ({ data: [] })),
        ]);
        setPost(postRes.data);
        setRelated(relatedRes.data);
      } catch { setPost(null); }
      setLoading(false);
    };
    fetch();
    window.scrollTo(0, 0);
  }, [slug]);

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: post?.title, url });
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B1120] flex items-center justify-center">
        <div className="w-10 h-10 border-3 border-[#FF9900] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-[#0B1120] flex flex-col items-center justify-center gap-4">
        <p className="text-white text-xl font-bold">Article Not Found</p>
        <Button onClick={() => navigate('/blog')} className="bg-[#FF9900] text-white rounded-xl">
          <ArrowLeft size={16} className="mr-2" /> {pt('blog.backToBlog')}
        </Button>
      </div>
    );
  }

  const publishDate = post.published_at ? new Date(post.published_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.meta_description || post.excerpt,
    author: { '@type': 'Organization', name: post.author || 'Track My Package Editorial' },
    publisher: { '@type': 'Organization', name: 'Track My Package', logo: { '@type': 'ImageObject', url: 'https://trackmypackage.app/assets/trackmypackage-logo.png' } },
    datePublished: post.published_at,
    dateModified: post.published_at,
    mainEntityOfPage: { '@type': 'WebPage', '@id': `https://trackmypackage.app/blog/${slug}` },
    keywords: (post.seo_keywords || []).join(', '),
  };

  return (
    <div className="min-h-screen bg-[#0B1120]" data-testid="blog-post-page">
      <SEOHead
        title={post.title}
        description={post.meta_description || post.excerpt}
        path={`/blog/${slug}`}
        type="article"
        keywords={(post.seo_keywords || []).join(', ')}
        jsonLd={jsonLd}
      />

      {/* Nav */}
      <PublicNav activePage="/blog" />

      {/* Article */}
      <article className="pt-24 pb-12 px-6">
        <div className="max-w-3xl mx-auto">
          {/* Back */}
          <motion.button onClick={() => navigate('/blog')}
            className="flex items-center gap-1.5 text-gray-400 hover:text-white text-sm mb-6 transition-colors"
            initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
            data-testid="blog-back-btn">
            <ArrowLeft size={16} /> {pt('blog.backToBlog')}
          </motion.button>

          {/* Header */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <span className="px-2.5 py-1 bg-[#FF9900]/10 text-[#FF9900] text-xs font-semibold rounded-full uppercase tracking-wide" data-testid="blog-post-category">
                {post.category}
              </span>
              <span className="flex items-center gap-1 text-gray-500 text-xs">
                <Clock size={12} /> {post.read_time_minutes || 5} min read
              </span>
              <span className="text-gray-500 text-xs">{publishDate}</span>
            </div>

            <h1 className="text-3xl sm:text-4xl font-bold text-white leading-tight mb-4" data-testid="blog-post-title">
              {post.title}
            </h1>

            {post.excerpt && (
              <p className="text-lg text-gray-400 mb-6 leading-relaxed" data-testid="blog-post-excerpt">{post.excerpt}</p>
            )}

            <div className="flex items-center justify-between border-y border-white/10 py-3 mb-8">
              <div className="text-sm text-gray-400">
                By <span className="text-white font-medium">{post.author || 'Track My Package Editorial'}</span>
              </div>
              <button onClick={handleShare}
                className="flex items-center gap-1.5 text-gray-400 hover:text-[#FF9900] text-sm transition-colors"
                data-testid="blog-share-btn">
                <ShareNetwork size={16} /> {pt('blog.shareArticle')}
              </button>
            </div>
          </motion.div>

          {/* Content */}
          <motion.div className="prose-custom" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            data-testid="blog-post-content">
            <MarkdownRenderer content={post.content} />
          </motion.div>

          {/* Tags */}
          {post.tags?.length > 0 && (
            <div className="flex items-center gap-2 mt-10 pt-6 border-t border-white/10 flex-wrap" data-testid="blog-post-tags">
              <Tag size={16} className="text-gray-500" />
              {post.tags.map(tag => (
                <button key={tag} onClick={() => navigate(`/blog?q=${tag}`)}
                  className="px-2.5 py-1 bg-white/5 text-gray-400 text-xs rounded-lg hover:bg-white/10 hover:text-white transition-all">
                  {tag}
                </button>
              ))}
            </div>
          )}

          {/* CTA Banner */}
          <div className="mt-10 bg-gradient-to-r from-[#003166] to-[#0060CC] rounded-2xl p-6 sm:p-8 text-center" data-testid="blog-inline-cta">
            <h3 className="text-xl font-bold text-white mb-2">Manage Your Courier Business Like a Pro</h3>
            <p className="text-white/60 text-sm mb-4">Track packages, manage clients, and generate invoices — all in one platform.</p>
            <Button onClick={() => window.open('https://sqawk.app/book/MrPositivity', '_blank')}
              className="bg-[#FF9900] hover:bg-[#FF9900]/90 text-white rounded-xl px-6 py-4">
              Schedule a Call <ArrowRight size={16} className="ml-2" />
            </Button>
          </div>
        </div>
      </article>

      {/* Related Articles */}
      {related.length > 0 && (
        <section className="py-12 px-6 bg-[#060B15]" data-testid="blog-related">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl font-bold text-white mb-6">{pt('blog.relatedArticles')}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {related.map((r, i) => (
                <motion.article key={r.id || i} onClick={() => navigate(`/blog/${r.slug}`)}
                  className="bg-white/[0.03] border border-white/10 rounded-xl p-4 hover:border-[#FF9900]/30 cursor-pointer transition-all"
                  initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                  <span className="text-[10px] text-[#FF9900] font-semibold uppercase">{r.category}</span>
                  <h3 className="font-bold text-white text-sm mt-1 leading-snug">{r.title}</h3>
                  <p className="text-xs text-gray-500 mt-2 line-clamp-2">{r.excerpt}</p>
                </motion.article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <PublicFooter />
    </div>
  );
}
